import { RestBase } from './base'
import { Message } from '@/ts/interfaces/message'
import { Poll } from '@/ts/interfaces/poll'
import { Quiz } from '@/ts/interfaces/quiz'
import { ScheduledMessage } from '@/ts/interfaces/scheduled-message'

export class RestChat {
  private rest: RestBase

  constructor() {
    this.rest = new RestBase()
  }

  public get(partyId: number, lastMessageId: string, limit: number = 20): Promise<{ items: (Message | Poll | Quiz)[] }> {
    let url = `/api/parties/${partyId}/chat/messages?limit=${limit}`

    if (lastMessageId) {
      url += `&before=${lastMessageId}`
    }

    return this.rest.get(url)
  }

  public getInitialMessages(partyId: number): Promise<{ items: (Message | Poll | Quiz)[] }> {
    return this.rest.get(`/api/parties/${partyId}/chat/recent`)
  }

  public sendMessage(token: string, partyId: number, text: string | null, sticker: string | null, replyId: string | null) {
    return this.rest.post(`/api/parties/${partyId}/chat/messages`, {
      headers: this.rest.bearerAuthorizationHeader(token),
      body: {
        reply_to_message_id: replyId,
        text,
        sticker,
      }
    })
  }

  public deleteMessage(token: string, partyId: number, messageId: string) {
    return this.rest.delete(`/api/parties/${partyId}/chat/messages/${messageId}`, { headers: this.rest.bearerAuthorizationHeader(token) })
  }

  public sendMedia(token: string, partyId: number, text: string | null, video: string | null, photo: string | null) {
    const body: { text?: string, video?: string, photo?: string } = {}

    if (text !== null && text.length) {
      body.text = text
    }

    if (video !== null && video.length) {
      body.video = video
    }

    if (photo !== null && photo.length) {
      body.photo = photo
    }

    return this.rest.post(`/api/parties/${partyId}/chat/media`, { body, headers: this.rest.bearerAuthorizationHeader(token) })
  }

  public sendPopup(token: string, partyId: number, message: object, isCloseable: boolean, isRestart: boolean, service: string, isFree: boolean) {
    return this.rest.post(`/api/parties/${partyId}/chat/popups`, {
      headers: this.rest.bearerAuthorizationHeader(token),
      body: {
        service: service === '' ? undefined : service,
        free_only: isFree,
        message,
        is_closeable: isCloseable,
        is_restart: isRestart,
      }
    })
  }

  public postPoll(token: string, partyId: number, question: string, options: string[], time: number) {
    return this.rest.post(`/api/parties/${partyId}/chat/polls`, {
      headers: this.rest.bearerAuthorizationHeader(token),
      body: {
        question,
        options,
        duration_seconds: time,
        set_next_track: false,
      }
    })
  }

  public votePoll(token: string, partyId: number, pollId: string, answerIndex: number) {
    return this.rest.post(`/api/parties/${partyId}/chat/polls/${pollId}/answers`, {
      headers: this.rest.bearerAuthorizationHeader(token),
      body: {
        answer_id: answerIndex,
      }
    })
  }

  public postQuiz(token: string, partyId: number, question: string, options: string[], correct_answer: number, time: number) {
    return this.rest.post(`/api/parties/${partyId}/chat/quiz/questions`, {
      headers: this.rest.bearerAuthorizationHeader(token),
      body: {
        question,
        options,
        correct_answer,
        set_next_track: false,
        duration_seconds: time,
      }
    })
  }

  public postQuizScoreboard(token: string, partyId: number, count: number) {
    return this.rest.post(`/api/parties/${partyId}/chat/quiz/scoreboard`, {
      headers: this.rest.bearerAuthorizationHeader(token),
      body: {
        count
      }
    })
  }

  public getQuizScoreboard(partyId: number, count: number) {
    return this.rest.get(`/api/parties/${partyId}/chat/quiz/scoreboard?count=${count}`)
  }

  public voteQuiz(token: string, partyId: number, questionId: string, answerIndex: number) {
    return this.rest.post(`/api/parties/${partyId}/chat/quiz/questions/${questionId}/answers`, {
      headers: this.rest.bearerAuthorizationHeader(token),
      body: {
        answer_id: answerIndex,
      }
    })
  }

  public deleteAll(partyId: number) {
    return this.rest.delete(`/api/parties/${partyId}/chat`, { withCmsAuth: true })
  }

  public getScheduled(partyId: number): Promise<{ items: ScheduledMessage[] }> {
    return this.rest.get(`/api/parties/${partyId}/chat/schedule`, { withCmsAuth: true })
  }

  public deleteScheduled(partyId: number, scheduledMessageId: number): Promise<{ items: ScheduledMessage[] }> {
    return this.rest.delete(`/api/parties/${partyId}/chat/schedule/${scheduledMessageId}`, { withCmsAuth: true })
  }

  public createScheduled(partyId: number, scheduledMessage: ScheduledMessage): Promise<ScheduledMessage> {
    return this.rest.post(`/api/parties/${partyId}/chat/schedule`, {
      withCmsAuth: true,
      body: {
        ...scheduledMessage,
        recurring_interval: scheduledMessage.recurring_interval === 0 ? null : scheduledMessage.recurring_interval,
      }
    })
  }

  public fakeStart(partyId: number, messagePerSecond: number, messageStopAfter: number): Promise<void> {
    return this.rest.post(`/api/parties/${partyId}/chat/fake`, {
      withCmsAuth: true,
      body: {
        messages_per_second: messagePerSecond,
        stop_after: messageStopAfter
      }
    })
  }

  public fakeStop(partyId: number): Promise<void> {
    return this.rest.delete(`/api/parties/${partyId}/chat/fake`, { withCmsAuth: true })
  }
}
